import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { MapRef, Marker } from 'react-map-gl';

import styled from 'styled-components';
import 'mapbox-gl/dist/mapbox-gl.css';

import { IconLocation } from '@components/icons/location';
import { SelectAsync, SelectAsyncProps } from '@components/select';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { debounce } from '@utils/browser';

export type LocationSearchProps = {
  placeholder: string;
  label: string;
  noLocationMatchingText: string;
  initialText: string;
  mapToken: string;
  onSelect: (location: LocationSearchValue) => void;
  height?: number;
  width?: number;
  defaultValue?: LocationSearchValue | null;
};

export type LocationSearchValue = {
  address: string;
  longitude: number;
  latitude: number;
};

type MapBoxFeatureResponse = {
  place_name: string;
  geometry: {
    coordinates: [number, number];
  };
};

export function LocationSearch({
  height = 500,
  width = 800,
  mapToken,
  onSelect,
  placeholder,
  label,
  noLocationMatchingText,
  initialText,
  defaultValue,
}: LocationSearchProps) {
  const mapRef = useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [longitude, setLongitude] = useState<number | null>(defaultValue?.longitude ?? null);
  const [latitude, setLatitude] = useState<number | null>(defaultValue?.latitude ?? null);
  const flyToMap = useCallback((longitude: number, latitude: number) => {
    mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 17, duration: 2500 });
  }, []);
  const onMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);
  useEffect(() => {
    if (
      mapLoaded &&
      defaultValue &&
      defaultValue.longitude !== null &&
      defaultValue.latitude !== null
    ) {
      flyToMap(defaultValue.longitude, defaultValue.latitude);
    }
  }, [defaultValue, mapLoaded, flyToMap]);
  const searchLocation: SelectAsyncProps['loadOptions'] = useMemo(
    () =>
      debounce((inputValue, callback) => {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${inputValue}.json?access_token=${mapToken}&proximity=ip&fuzzyMatch=false`;
        fetch(url)
          .then((response) => response.json())
          .then((data) => {
            data = data.features.map((d: MapBoxFeatureResponse) => ({
              label: (
                <Stack space={2} orientation="horizontal" align="center">
                  <IconLocation color="purple100" />
                  {d.place_name}
                </Stack>
              ),
              value: {
                address: d.place_name,
                longitude: d.geometry.coordinates[0],
                latitude: d.geometry.coordinates[1],
              },
            }));
            callback(data);
          });
      }, 300),
    [mapToken]
  );
  return (
    <Stack space={4}>
      <SelectAsync
        placeholder={defaultValue?.address ?? placeholder}
        label={label}
        defaultOptions={false}
        loadOptions={searchLocation}
        noOptions={({ inputValue }) => {
          if (inputValue !== '') return `${noLocationMatchingText} ` + inputValue;
          return initialText;
        }}
        onChange={(value: LocationSearchValue) => {
          flyToMap(value.longitude, value.latitude);
          setLongitude(value.longitude);
          setLatitude(value.latitude);
          onSelect(value);
        }}
      />
      <MapContainer space={0}>
        <Map
          ref={mapRef}
          onLoad={onMapLoad}
          key={mapToken}
          style={{ height, width }}
          interactive={false}
          mapboxAccessToken={mapToken}
          mapStyle="mapbox://styles/mapbox/streets-v9"
        >
          {latitude !== null && longitude !== null && (
            <Marker key={latitude} latitude={latitude} longitude={longitude} anchor="bottom">
              <IconLocation color="elementBlue100" />
            </Marker>
          )}
        </Map>
      </MapContainer>
    </Stack>
  );
}

const MapContainer = styled(Stack)({
  backgroundColor: theme.color.purple10,
  borderRadius: '0.25rem',
  padding: '1rem',
  width: '100%',
  height: '100%',
  '.mapboxgl-ctrl-bottom-right, .mapboxgl-ctrl-bottom-left': {
    display: 'none',
  },
});
